import React, { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { CheckboxComponent } from "../FormComponent";
import DEFAULT_ROUTES from "../../constants/routes";
import {
  fetchCurrentLegalDocuments,
  getLegalVersionIds,
} from "../../utils/api/legal_apis";
import { STATUS } from "../../utils/api/sharedfunction";
import { CONSENT_COPY } from "../../constants/consentCopy";
import LegalDocumentLink from "./LegalDocumentLink";
import styles from "../../styles/consent/legalConsent.module.scss";

const defaultConsentState = {
  agreed: false,
  loading: true,
  error: null,
  tosLegalDocumentVersionId: null,
  privacyLegalDocumentVersionId: null,
};

/**
 * Checkbox with links to /terms and /privacy. Loads current legal version IDs from API.
 */
const LegalConsentCheckbox = ({
  checked = false,
  onChange,
  disabled = false,
  error,
  id = "legal-consent",
  emphasized = false,
}) => {
  const [meta, setMeta] = useState(defaultConsentState);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setMeta((prev) => ({ ...prev, loading: true, error: null }));
      const response = await fetchCurrentLegalDocuments();

      if (cancelled) {
        return;
      }

      if (response.status !== STATUS.SUCCESS) {
        const errorMeta = {
          agreed: false,
          loading: false,
          error: response.error || "Unable to load legal documents",
          tosLegalDocumentVersionId: null,
          privacyLegalDocumentVersionId: null,
        };
        setMeta(errorMeta);
        onChangeRef.current?.({ ...errorMeta, ready: false });
        return;
      }

      const versionIds = getLegalVersionIds(response.data);
      const nextMeta = {
        agreed: false,
        loading: false,
        error: null,
        ...versionIds,
      };
      setMeta(nextMeta);
      onChangeRef.current?.({
        ...nextMeta,
        agreed: checked,
        ready: Boolean(versionIds.tosLegalDocumentVersionId && versionIds.privacyLegalDocumentVersionId),
      });
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  if (meta.loading) {
    return (
      <div className={styles.consentLoading}>
        <CircularProgress size={18} />
        <span className={styles.consentLoadingText}>Loading legal documents…</span>
      </div>
    );
  }

  const label = (
    <span className={styles.consentCheckboxLabel}>
      {CONSENT_COPY.checkboxPrefix}{" "}
      <LegalDocumentLink to={DEFAULT_ROUTES.TERMS}>Terms of Service</LegalDocumentLink>{" "}
      and{" "}
      <LegalDocumentLink to={DEFAULT_ROUTES.PRIVACY}>Privacy Policy</LegalDocumentLink>
      {CONSENT_COPY.checkboxSuffix}
    </span>
  );

  const checkbox = (
    <CheckboxComponent
      name={id}
      checked={checked}
      onChange={(e) => onChange?.({
        agreed: e.target.checked,
        loading: meta.loading,
        error: meta.error,
        tosLegalDocumentVersionId: meta.tosLegalDocumentVersionId,
        privacyLegalDocumentVersionId: meta.privacyLegalDocumentVersionId,
        ready:
          !meta.loading &&
          !meta.error &&
          Boolean(meta.tosLegalDocumentVersionId && meta.privacyLegalDocumentVersionId),
      })}
      disabled={disabled || Boolean(meta.error)}
      label={label}
      error={error || meta.error}
      required
    />
  );

  if (emphasized) {
    return <div className={styles.consentSection}>{checkbox}</div>;
  }

  return checkbox;
};

export default LegalConsentCheckbox;
